import { useEffect, useState } from 'react';
import {
  fetchAndPublishScopedCatalog,
  fetchAndPublishUnscopedCatalog,
} from '../utils/pksSolutionsCatalog';
import { useShellStore } from '../../../shell/store/shellStore';
import headerData from '../../Navigation/data/globalHeaderData.json';
import type { GlobalHeaderData } from '../../Navigation/data/types';
import { resolveCatalogApplicationUkey } from '../utils/moduleApplicationMap';

export interface UseSolutionsCatalogSyncResult {
  /** True once the unscoped catalog fetch settles (success or failure). Never hangs. */
  isReady: boolean;
}

const CANVAS_ROUTE_RE = /\/operational\/canvas\/(.+)$/;

function getKnownModuleKeys(): string[] {
  const data = headerData as GlobalHeaderData;
  const keys: string[] = [];
  for (const app of data.applications) {
    for (const item of app.items) {
      if (item.type === 'module' && item.route) {
        const match = CANVAS_ROUTE_RE.exec(item.route);
        if (match) keys.push(match[1]);
      }
    }
  }
  return keys;
}

/**
 * Dual catalog sync:
 * 1) Unscoped fetch (no application_ukey) for the waffle menu — once per tenant.
 * 2) Scoped fetch (with application_ukey) when shell selection resolves an app —
 *    also triggered explicitly from GlobalHeader on every waffle click.
 */
export function useSolutionsCatalogSync(): UseSolutionsCatalogSyncResult {
  const tenant = useShellStore(state => state.tenant);
  const tenantId = tenant?.id ?? '';
  const activeModule = useShellStore(state => state.activeModule);
  const activeSolution = useShellStore(state => state.activeSolution);
  const activeCanvas = useShellStore(state => state.activeCanvas);

  const applicationUkey =
    resolveCatalogApplicationUkey({
      activeModule,
      activeSolution,
      activeCanvas,
    }) ?? '';

  const [isReady, setIsReady] = useState(() => !tenantId);

  // ── Unscoped catalog for waffle ───────────────────────────────────────────
  useEffect(() => {
    if (!tenantId) {
      setIsReady(true);
      return;
    }

    let cancelled = false;
    setIsReady(false);

    const run = async () => {
      try {
        await fetchAndPublishUnscopedCatalog(tenantId, getKnownModuleKeys());
      } catch {
        // Silently fail — consumers fall back to hardcoded lists.
      } finally {
        if (!cancelled) setIsReady(true);
      }
    };

    void run();
    return () => { cancelled = true; };
  }, [tenantId]);

  // ── Scoped catalog when shell context implies an application ──────────────
  useEffect(() => {
    if (!tenantId || !applicationUkey) return;

    const run = async () => {
      try {
        await fetchAndPublishScopedCatalog(tenantId, applicationUkey);
      } catch {
        // Runs canvas falls back to unscoped bridge / hardcoded lists.
      }
    };

    void run();
  }, [tenantId, applicationUkey, activeModule, activeSolution]);

  return { isReady };
}
